import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  transportApi,
  type AnomalieCarburantListParams,
  type BonCarburantListParams,
  type BonTravailListParams,
  type PlanningListParams,
  type CamionListParams,
  type CreateBonCarburantPayload,
  type CreateBonTravailPayload,
  type CreateCamionPayload,
  type CreateDepotPayload,
  type CreateEquipePayload,
  type CreateMinePayload,
  type CreateMouvementPayload,
  type CreateVoyagePayload,
  type EquipeListParams,
  type MineListParams,
  type MouvementListParams,
  type UpdateAnomalieCarburantPayload,
  type UpdateBonCarburantPayload,
  type UpdateBonTravailPayload,
  type UpdateCamionPayload,
  type UpdateDepotPayload,
  type UpdateEquipePayload,
  type UpdateMinePayload,
  type UpdateVoyagePayload,
  type VoyageListParams,
} from '@/api/transport';

const EQUIPES_KEY   = ['operations', 'transport', 'equipes']   as const;
const CAMIONS_KEY   = ['operations', 'transport', 'camions']   as const;
const MINES_KEY     = ['operations', 'transport', 'mines']     as const;
const VOYAGES_KEY   = ['operations', 'transport', 'voyages']   as const;
const DASHBOARD_KEY = ['operations', 'transport', 'dashboard'] as const;

// ── Equipes ────────────────────────────────────────────────────────────────

export const useEquipes = (params: EquipeListParams = {}) =>
  useQuery({
    queryKey: [...EQUIPES_KEY, params],
    queryFn:  () => transportApi.listEquipes(params),
    staleTime: 30_000,
  });

export const useEquipe = (id: string | undefined) =>
  useQuery({
    queryKey: [...EQUIPES_KEY, id],
    queryFn:  () => transportApi.getEquipe(id!),
    enabled:  !!id,
    staleTime: 15_000,
  });

export const useCreateEquipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEquipePayload) => transportApi.createEquipe(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};

export const useUpdateEquipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEquipePayload }) =>
      transportApi.updateEquipe(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};

export const useDeleteEquipe = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteEquipe(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};

// ── Camions ────────────────────────────────────────────────────────────────

export const useCamions = (params: CamionListParams = {}) =>
  useQuery({
    queryKey: [...CAMIONS_KEY, params],
    queryFn:  () => transportApi.listCamions(params),
    staleTime: 30_000,
  });

export const useCamion = (id: string | undefined) =>
  useQuery({
    queryKey: [...CAMIONS_KEY, id],
    queryFn:  () => transportApi.getCamion(id!),
    enabled:  !!id,
    staleTime: 15_000,
  });

export const useCreateCamion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCamionPayload) => transportApi.createCamion(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: CAMIONS_KEY });
      qc.invalidateQueries({ queryKey: EQUIPES_KEY }); // refetch counts
    },
  });
};

export const useUpdateCamion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCamionPayload }) =>
      transportApi.updateCamion(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: CAMIONS_KEY });
      qc.invalidateQueries({ queryKey: EQUIPES_KEY });
    },
  });
};

export const useDeleteCamion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteCamion(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: CAMIONS_KEY });
      qc.invalidateQueries({ queryKey: EQUIPES_KEY });
    },
  });
};

// ── Mines ──────────────────────────────────────────────────────────────────

export const useMines = (params: MineListParams = {}) =>
  useQuery({
    queryKey: [...MINES_KEY, params],
    queryFn:  () => transportApi.listMines(params),
    staleTime: 60_000,
  });

export const useCreateMine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMinePayload) => transportApi.createMine(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MINES_KEY }),
  });
};

export const useUpdateMine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMinePayload }) =>
      transportApi.updateMine(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MINES_KEY }),
  });
};

export const useDeleteMine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteMine(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MINES_KEY }),
  });
};

// ── Voyages ────────────────────────────────────────────────────────────────

export const useVoyages = (params: VoyageListParams = {}) =>
  useQuery({
    queryKey: [...VOYAGES_KEY, params],
    queryFn:  () => transportApi.listVoyages(params),
    staleTime: 15_000,
    refetchInterval: 30_000, // auto-refresh terrain
  });

export const useVoyage = (id: string | undefined) =>
  useQuery({
    queryKey: [...VOYAGES_KEY, id],
    queryFn:  () => transportApi.getVoyage(id!),
    enabled:  !!id,
  });

export const useCreateVoyage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVoyagePayload) => transportApi.createVoyage(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: VOYAGES_KEY });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    },
  });
};

export const useUpdateVoyage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVoyagePayload }) =>
      transportApi.updateVoyage(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: VOYAGES_KEY });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    },
  });
};

export const useDeleteVoyage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteVoyage(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: VOYAGES_KEY });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    },
  });
};

// ── Dashboard ──────────────────────────────────────────────────────────────

export const useTransportDashboard = (date?: string) =>
  useQuery({
    queryKey: [...DASHBOARD_KEY, date ?? 'today'],
    queryFn:  () => transportApi.getDashboard(date),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000, // brief §1.1 — auto-refresh 5 min
  });

// ── Rapport journalier ────────────────────────────────────────────────────

const RAPPORT_KEY = ['operations', 'transport', 'rapport'] as const;

export const useRapportJournalier = (date?: string) =>
  useQuery({
    queryKey: [...RAPPORT_KEY, date ?? 'today'],
    queryFn:  () => transportApi.getRapport(date),
    staleTime: 60_000,
  });

// ── Carburant ─────────────────────────────────────────────────────────────

const CARBURANT_DASH_KEY = ['operations', 'transport', 'carburant', 'dashboard'] as const;
const BONS_KEY            = ['operations', 'transport', 'carburant', 'bons'] as const;
const ANOMALIES_KEY       = ['operations', 'transport', 'carburant', 'anomalies'] as const;

export const useCarburantDashboard = (date?: string) =>
  useQuery({
    queryKey: [...CARBURANT_DASH_KEY, date ?? 'today'],
    queryFn:  () => transportApi.getCarburantDashboard(date),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

export const useBonsCarburant = (params: BonCarburantListParams = {}) =>
  useQuery({
    queryKey: [...BONS_KEY, params],
    queryFn:  () => transportApi.listBonsCarburant(params),
    staleTime: 30_000,
  });

export const useBonCarburant = (id: string | undefined) =>
  useQuery({
    queryKey: [...BONS_KEY, id],
    queryFn:  () => transportApi.getBonCarburant(id!),
    enabled:  !!id,
  });

export const useCreateBonCarburant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBonCarburantPayload) => transportApi.createBonCarburant(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BONS_KEY });
      qc.invalidateQueries({ queryKey: CARBURANT_DASH_KEY });
      qc.invalidateQueries({ queryKey: ANOMALIES_KEY });
    },
  });
};

export const useUpdateBonCarburant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBonCarburantPayload }) =>
      transportApi.updateBonCarburant(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BONS_KEY });
      qc.invalidateQueries({ queryKey: CARBURANT_DASH_KEY });
    },
  });
};

export const useDeleteBonCarburant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteBonCarburant(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BONS_KEY });
      qc.invalidateQueries({ queryKey: CARBURANT_DASH_KEY });
      qc.invalidateQueries({ queryKey: ANOMALIES_KEY });
    },
  });
};

export const useAnomaliesCarburant = (params: AnomalieCarburantListParams = {}) =>
  useQuery({
    queryKey: [...ANOMALIES_KEY, params],
    queryFn:  () => transportApi.listAnomaliesCarburant(params),
    staleTime: 30_000,
  });

export const useUpdateAnomalieCarburant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAnomalieCarburantPayload }) =>
      transportApi.updateAnomalieCarburant(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ANOMALIES_KEY });
      qc.invalidateQueries({ queryKey: CARBURANT_DASH_KEY });
    },
  });
};

// ── Dépôts ────────────────────────────────────────────────────────────────

const DEPOTS_KEY      = ['operations', 'transport', 'carburant', 'depots'] as const;
const MOUVEMENTS_KEY  = ['operations', 'transport', 'carburant', 'mouvements'] as const;
const RAPPORT_MENS_KEY = ['operations', 'transport', 'carburant', 'rapport-mensuel'] as const;

export const useDepots = () =>
  useQuery({
    queryKey: DEPOTS_KEY,
    queryFn:  () => transportApi.listDepots(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

export const useCreateDepot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDepotPayload) => transportApi.createDepot(payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DEPOTS_KEY }); },
  });
};

export const useUpdateDepot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDepotPayload }) =>
      transportApi.updateDepot(id, payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DEPOTS_KEY }); },
  });
};

export const useDeleteDepot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteDepot(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DEPOTS_KEY }); },
  });
};

export const useMouvementsDepot = (params: MouvementListParams = {}) =>
  useQuery({
    queryKey: [...MOUVEMENTS_KEY, params],
    queryFn:  () => transportApi.listMouvements(params),
    staleTime: 30_000,
  });

export const useCreateMouvement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMouvementPayload) => transportApi.createMouvement(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: MOUVEMENTS_KEY });
      qc.invalidateQueries({ queryKey: DEPOTS_KEY });
    },
  });
};

export const useDeleteMouvement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteMouvement(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: MOUVEMENTS_KEY });
      qc.invalidateQueries({ queryKey: DEPOTS_KEY });
    },
  });
};

export const useRapportMensuelCarburant = (year: number, month: number) =>
  useQuery({
    queryKey: [...RAPPORT_MENS_KEY, year, month],
    queryFn:  () => transportApi.getRapportMensuelCarburant(year, month),
    staleTime: 60_000,
  });

// ── Maintenance ───────────────────────────────────────────────────────────

const MAINT_DASH_KEY = ['operations', 'transport', 'maintenance', 'dashboard'] as const;
const BT_KEY         = ['operations', 'transport', 'maintenance', 'bons-travail'] as const;

export const useMaintenanceDashboard = (date?: string) =>
  useQuery({
    queryKey: [...MAINT_DASH_KEY, date ?? 'today'],
    queryFn:  () => transportApi.getMaintenanceDashboard(date),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

export const useBonsTravail = (params: BonTravailListParams = {}) =>
  useQuery({
    queryKey: [...BT_KEY, params],
    queryFn:  () => transportApi.listBonsTravail(params),
    staleTime: 30_000,
  });

export const useBonTravail = (id: string | undefined) =>
  useQuery({
    queryKey: [...BT_KEY, id],
    queryFn:  () => transportApi.getBonTravail(id!),
    enabled:  !!id,
  });

export const useCreateBonTravail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBonTravailPayload) => transportApi.createBonTravail(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BT_KEY });
      qc.invalidateQueries({ queryKey: MAINT_DASH_KEY });
    },
  });
};

export const useUpdateBonTravail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBonTravailPayload }) =>
      transportApi.updateBonTravail(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BT_KEY });
      qc.invalidateQueries({ queryKey: MAINT_DASH_KEY });
    },
  });
};

export const useDeleteBonTravail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteBonTravail(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: BT_KEY });
      qc.invalidateQueries({ queryKey: MAINT_DASH_KEY });
    },
  });
};

// ── Planning préventif ────────────────────────────────────────────────────

const ALERTES_PLANNING_KEY = ['operations', 'transport', 'maintenance', 'planning', 'alertes'] as const;
const PLANNING_KEY         = ['operations', 'transport', 'maintenance', 'planning'] as const;

export const useAlertesPlanning = () =>
  useQuery({
    queryKey: ALERTES_PLANNING_KEY,
    queryFn:  () => transportApi.getAlertesPlanning(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

export const usePlanning = (params: PlanningListParams = {}) =>
  useQuery({
    queryKey: [...PLANNING_KEY, params],
    queryFn:  () => transportApi.listPlanning(params),
    staleTime: 30_000,
  });

export const useGenererBTPlanning = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planningId, mecanicienPrincipalNom }: { planningId: string; mecanicienPrincipalNom: string }) =>
      transportApi.genererBTPlanning(planningId, mecanicienPrincipalNom),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: PLANNING_KEY });
      qc.invalidateQueries({ queryKey: BT_KEY });
      qc.invalidateQueries({ queryKey: MAINT_DASH_KEY });
      qc.invalidateQueries({ queryKey: ALERTES_PLANNING_KEY });
    },
  });
};

export const useAnnulerPlanning = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planningId: string) => transportApi.annulerPlanning(planningId),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: PLANNING_KEY });
      qc.invalidateQueries({ queryKey: ALERTES_PLANNING_KEY });
    },
  });
};

export const useTriggerN2Hebdo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => transportApi.triggerN2Hebdo(),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: PLANNING_KEY });
      qc.invalidateQueries({ queryKey: ALERTES_PLANNING_KEY });
    },
  });
};

const RAPPORT_MENS_MAINT_KEY = ['operations', 'transport', 'maintenance', 'rapport-mensuel'] as const;

export const useRapportMensuelMaintenance = (year: number, month: number) =>
  useQuery({
    queryKey: [...RAPPORT_MENS_MAINT_KEY, year, month],
    queryFn:  () => transportApi.getRapportMensuelMaintenance(year, month),
    staleTime: 60_000,
  });

// ── Destinataires + envoi rapport journalier ──────────────────────────────

const DESTINATAIRES_KEY = ['operations', 'transport', 'rapport', 'destinataires'] as const;
const ENVOI_LOGS_KEY    = ['operations', 'transport', 'rapport', 'logs'] as const;

export const useDestinataires = () =>
  useQuery({
    queryKey: DESTINATAIRES_KEY,
    queryFn:  () => transportApi.listDestinataires(),
    staleTime: 30_000,
  });

export const useCreateDestinataire = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; fullName?: string; role?: string; actif?: boolean }) =>
      transportApi.createDestinataire(payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DESTINATAIRES_KEY }); },
  });
};

export const useUpdateDestinataire = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { fullName?: string; role?: string; actif?: boolean } }) =>
      transportApi.updateDestinataire(id, payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DESTINATAIRES_KEY }); },
  });
};

export const useDeleteDestinataire = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transportApi.deleteDestinataire(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: DESTINATAIRES_KEY }); },
  });
};

export const useSendRapportNow = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date?: string) => transportApi.sendRapportNow(date),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ENVOI_LOGS_KEY }); },
  });
};

export const useEnvoiLogs = () =>
  useQuery({
    queryKey: ENVOI_LOGS_KEY,
    queryFn:  () => transportApi.listEnvoiLogs(),
    staleTime: 30_000,
  });
